import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { FileUpload, type UploadResult } from '@/components/FileUpload';
import { useDialogFocusReturn } from '@/hooks/useDialogFocusReturn';

import type {
  AddableQuestionType,
  EditableGradingRubric,
  EditableQuestion,
  EditableQuestionImage,
  EditableQuestionPart,
} from './editorTypes';
import { AltTextField } from './AltTextField';
import { QuestionImagesEditor } from './QuestionImagesEditor';
import { AIUploadQuestionEditor } from './aiUpload/AIUploadQuestionEditor';
import { ExactAnswerEditor } from './ExactAnswerEditor';
import { HintSolutionEditor } from './HintSolutionEditor';
import { MultiPartEditor } from './MultiPartEditor';
import { MultipleChoiceEditor } from './MultipleChoiceEditor';
import { NumericalAnswerEditor } from './NumericalAnswerEditor';
import { STRUCTURAL_EDIT_LOCK_REASON } from './questionEditRestrictions';
import {
  cleanQuestionForType,
  cloneEditableQuestion,
  createDraftGradingRubric,
  createDraftQuestion,
  createDraftQuestionPart,
  getRubricTotalScore,
  getQuestionTypeLabel,
  isEditableQuestionType,
  normalizeGradingRubric,
  validateQuestionDraft,
} from './questionDialogUtils';
import { QuestionReadonlyPreview } from './QuestionPreviewCard';
import { TextUnitEditor } from './TextUnitEditor';
import { VariablesPanel } from './VariablesPanel';
import {
  detectQuestionVariableNames,
  getMissingDetectedVariables,
} from './useContentEditor';
import styles from './QuestionEditDialog.module.css';

interface QuestionEditDialogProps {
  disabled?: boolean;
  /**
   * Where focus goes on close when the control that opened the editor has
   * gone: a question's Edit button is not drawn while it is being edited.
   */
  focusFallback?: () => HTMLElement | null;
  mode: 'edit' | 'new';
  onOpenChange(nextOpen: boolean): void;
  onSave(question: EditableQuestion): void;
  open: boolean;
  question?: EditableQuestion;
  questionLabel?: string;
  structuralEditsRestricted?: boolean;
}

const QUESTION_TYPES: Array<{
  // One line saying how the answer is judged, which is the part that actually
  // differs between the types and the part an instructor gets wrong.
  description: string;
  label: string;
  value: AddableQuestionType;
}> = [
  {
    description:
      'Students pick one option. You mark which option is correct.',
    label: 'Multiple Choice',
    value: 'MULTIPLE_CHOICE',
  },
  {
    description:
      'Students pick every option that applies. You mark each correct option. Credit only for selecting all of them and nothing else — there is no partial credit.',
    label: 'Select All That Apply',
    value: 'SELECT_ALL',
  },
  {
    description:
      'Students enter a number. Anything within the error tolerance of the correct answer earns full credit.',
    label: 'Numerical',
    value: 'NUMERICAL_WITH_ERROR',
  },
  {
    description:
      'Students enter text, which must match your answer exactly apart from letter case. No partial or near matches.',
    label: 'Exact Answer',
    value: 'EXACT_ANSWER',
  },
  {
    description:
      'One prompt with several sub-questions, each with its own type, answer, and points.',
    label: 'Multi-Part',
    value: 'MULTI_PART',
  },
  {
    description:
      'Students upload a photo of their work and AI grades it against your rubric.',
    label: 'AI Upload',
    value: 'AI_UPLOAD',
  },
];

/**
 * What a multi-part question's parts are worth together. A part with no points
 * of its own counts as one, as the page and the grading count it
 * (`questionPointsPossible` on the backend).
 */
function getPartsTotal(parts: EditableQuestionPart[] | undefined) {
  const total = (parts ?? []).reduce(
    (sum, part) => sum + (part.value !== undefined && part.value > 0 ? part.value : 1),
    0,
  );

  // Added in floating point; rounded back to the halves and rubric decimals
  // that were typed.
  return Math.round(total * 1e6) / 1e6;
}

/**
 * A multi-part question is worth its parts, which is how the grading already
 * counts it. Its points follow its parts' total whenever that total changes.
 * A saved question whose points differ keeps them until then, or until the
 * instructor takes the total: no question's points change without the
 * instructor doing something to them.
 */
function followPartsTotal(
  question: EditableQuestion,
  parts: EditableQuestionPart[],
): Partial<EditableQuestion> {
  const total = getPartsTotal(parts);

  return total === getPartsTotal(question.parts) ? {} : { value: total };
}

function formatPoints(points: number) {
  return `${points} point${points === 1 ? '' : 's'}`;
}

function moveArrayItem<T>(items: T[], fromIndex: number, toIndex: number) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);

  if (movedItem === undefined) {
    return nextItems;
  }

  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function withTypeDefaults(
  draft: EditableQuestion,
  type: AddableQuestionType,
): EditableQuestion {
  if (type === 'MULTIPLE_CHOICE') {
    return {
      ...draft,
      correctAnswerIndex:
        draft.correctAnswerIndex ?? draft.correctAnswerIndices?.[0] ?? 0,
      options: draft.options.length >= 2 ? draft.options : ['', ''],
      type,
    };
  }

  if (type === 'SELECT_ALL') {
    return {
      ...draft,
      // Switching from multiple choice carries its correct option over rather
      // than making the instructor mark it again.
      correctAnswerIndices:
        draft.correctAnswerIndices && draft.correctAnswerIndices.length > 0
          ? draft.correctAnswerIndices
          : draft.correctAnswerIndex === undefined
            ? []
            : [draft.correctAnswerIndex],
      options: draft.options.length >= 2 ? draft.options : ['', ''],
      type,
    };
  }

  if (type === 'MULTI_PART') {
    const parts =
      draft.parts && draft.parts.length > 0
        ? draft.parts
        : [createDraftQuestionPart('EXACT_ANSWER')];

    return {
      ...draft,
      parts,
      type,
      value: getPartsTotal(parts),
    };
  }

  if (type === 'AI_UPLOAD') {
    const gradingRubric =
      draft.gradingRubric ?? createDraftGradingRubric();

    return {
      ...draft,
      correctAnswerIndex: undefined,
      exactAnswer: undefined,
      gradingRubric,
      maxNumAttempts: draft.maxNumAttempts ?? 1,
      numericalAnswer: undefined,
      numericalError: undefined,
      options: [],
      parts: undefined,
      type,
      useRandomVariables: false,
      value: getRubricTotalScore(gradingRubric),
      variableBasedAnswer: undefined,
      variables: [],
    };
  }

  return {
    ...draft,
    type,
  };
}

function getInitialDraft(
  mode: QuestionEditDialogProps['mode'],
  question: EditableQuestion | undefined,
) {
  if (!question) {
    return createDraftQuestion('MULTIPLE_CHOICE');
  }

  const draft = cloneEditableQuestion(question);
  const hasOwnPoints = draft.value !== undefined && draft.value > 0;

  // A new multi-part question opens worth its parts, and so does a saved one
  // with no points of its own, which the page and the grading already count
  // as its parts' total. A saved one with points of its own keeps them.
  if (draft.type === 'MULTI_PART' && (mode === 'new' || !hasOwnPoints)) {
    return { ...draft, value: getPartsTotal(draft.parts) };
  }

  return draft;
}

function getDraftKey(question: EditableQuestion | undefined, mode: string) {
  return `${mode}:${question?.id ?? 'new'}`;
}

function serializeDraft(question: EditableQuestion) {
  return JSON.stringify(question);
}

function applyValidationErrorsToDraft(
  draft: EditableQuestion,
  validated: EditableQuestion,
) {
  return {
    ...draft,
    parts: draft.parts?.map((part, index) => ({
      ...part,
      validationError: validated.parts?.[index]?.validationError,
    })),
    validationError: validated.validationError,
  };
}

function makeImageId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `prompt-image-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function QuestionEditDialog({
  disabled = false,
  focusFallback,
  mode,
  onOpenChange,
  onSave,
  open,
  question,
  questionLabel,
  structuralEditsRestricted = false,
}: QuestionEditDialogProps) {
  const focusReturn = useDialogFocusReturn({ fallback: focusFallback });
  const editTabId = useId();
  const previewTabId = useId();
  const editPanelId = useId();
  const previewPanelId = useId();
  const pointsNoteId = useId();
  const saveReasonId = useId();
  const lockNoteTitleId = useId();
  const pointsFieldRef = useRef<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
  // The image uploads still running for the question open now: Save waits
  // for them, or the question would be saved without their images.
  const [uploadsUnderway, setUploadsUnderway] = useState(0);
  // This opening of the dialog. It stays mounted while closed, so an upload
  // still running when it closes would otherwise reach whichever question it
  // shows when the upload ends.
  const [opening, setOpening] = useState(() => ({}));
  const openingRef = useRef(opening);
  const [draft, setDraft] = useState<EditableQuestion>(() =>
    getInitialDraft(mode, question),
  );
  // The points a question had when it became multi-part in this dialog. They
  // are its again if it stops being one: a 3-point question tried as
  // multi-part and switched back must still earn 3 for a right answer.
  const [pointsBeforeParts, setPointsBeforeParts] = useState<number | undefined>();
  const [initialSerializedDraft, setInitialSerializedDraft] = useState(() =>
    serializeDraft(getInitialDraft(mode, question)),
  );
  const structuralDisabled = disabled || structuralEditsRestricted;
  const selectedType = QUESTION_TYPES.find(
    (option) => option.value === draft.type,
  );
  const isDirty = useMemo(
    () => serializeDraft(draft) !== initialSerializedDraft,
    [draft, initialSerializedDraft],
  );
  const isUnsupported = !isEditableQuestionType(draft.type);
  const partsTotal =
    draft.type === 'MULTI_PART' ? getPartsTotal(draft.parts) : undefined;
  const pointsDiffer = partsTotal !== undefined && draft.value !== partsTotal;
  const title = mode === 'new'
    ? 'New Question'
    : questionLabel
      ? `Edit ${questionLabel}`
      : 'Edit Question';
  const detectedVariableNames = useMemo(
    () => detectQuestionVariableNames(draft),
    [draft.contentHtml, draft.options, draft.parts],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextDraft = getInitialDraft(mode, question);
    const nextOpening = {};
    openingRef.current = nextOpening;
    setOpening(nextOpening);
    setUploadsUnderway(0);
    setDraft(nextDraft);
    setInitialSerializedDraft(serializeDraft(nextDraft));
    setPointsBeforeParts(undefined);
    setActiveTab('edit');
  }, [mode, open, question]);

  // Keep an editable definition (with Min/Max inputs) for every variable the
  // content references via ##name##. Detected names that have no row yet are
  // appended so the instructor can set their range; existing rows (and their
  // user-entered Min/Max) and any manually-added variables are preserved.
  useEffect(() => {
    if (!open || !draft.useRandomVariables) {
      return;
    }

    if (getMissingDetectedVariables(draft).length === 0) {
      return;
    }

    setDraft((currentDraft) => {
      const additions = getMissingDetectedVariables(currentDraft);

      if (additions.length === 0) {
        return currentDraft;
      }

      return {
        ...currentDraft,
        variables: [...currentDraft.variables, ...additions],
      };
    });
  }, [detectedVariableNames, draft, draft.useRandomVariables, open]);

  function requestClose() {
    if (!isDirty || window.confirm('Discard unsaved changes?')) {
      onOpenChange(false);
    }
  }

  function updateDraft(updates: Partial<EditableQuestion>) {
    setDraft((currentDraft) => ({
      ...currentDraft,
      ...updates,
      validationError: undefined,
    }));
  }

  function updateQuestionPart(
    partIndex: number,
    updates: Partial<EditableQuestionPart>,
  ) {
    setDraft((currentDraft) => {
      const parts = (currentDraft.parts ?? []).map((part, currentIndex) =>
        currentIndex === partIndex
          ? {
              ...part,
              ...updates,
              validationError: undefined,
            }
          : part,
      );

      return {
        ...currentDraft,
        ...followPartsTotal(currentDraft, parts),
        parts,
        validationError: undefined,
      };
    });
  }

  function handleSave() {
    const cleaned = cleanQuestionForType(draft);
    const validated = validateQuestionDraft(cleaned);

    if (
      validated.validationError ||
      (validated.parts ?? []).some((part) => part.validationError)
    ) {
      setDraft(applyValidationErrorsToDraft(draft, validated));
      setActiveTab('edit');
      return;
    }

    onSave(validated);
    onOpenChange(false);
  }

  // An upload reports to the opening it began in, and to no other.
  function forThisOpening<Args extends unknown[]>(handler: (...args: Args) => void) {
    return (...args: Args) => {
      if (opening === openingRef.current) {
        handler(...args);
      }
    };
  }

  const imageUploadCount = {
    onUploadEnd: forThisOpening(() => {
      setUploadsUnderway((count) => Math.max(0, count - 1));
    }),
    onUploadStart: forThisOpening(() => {
      setUploadsUnderway((count) => count + 1);
    }),
  };

  const handlePromptImageUpload = forThisOpening((result: UploadResult) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      promptImages: [
        ...(currentDraft.promptImages ?? []),
        { id: makeImageId(), imageUrl: result.downloadUrl },
      ],
      validationError: undefined,
    }));
  });

  // Hints and worked solutions are content sections too, so their images are
  // managed exactly like the prompt's.
  function makeSectionImageHandlers(
    key: 'detailedAnswerImages' | 'hintImages',
  ) {
    return {
      onChange(imageId: string, updates: Partial<EditableQuestionImage>) {
        updateDraft({
          [key]: (draft[key] ?? []).map((image) =>
            image.id === imageId ? { ...image, ...updates } : image,
          ),
        });
      },
      onRemove(imageId: string) {
        updateDraft({
          [key]: (draft[key] ?? []).filter((image) => image.id !== imageId),
        });
      },
      onUpload: forThisOpening((result: UploadResult) => {
        setDraft((currentDraft) => ({
          ...currentDraft,
          [key]: [
            ...(currentDraft[key] ?? []),
            { id: makeImageId(), imageUrl: result.downloadUrl },
          ],
        }));
      }),
    };
  }

  const hintImageHandlers = makeSectionImageHandlers('hintImages');
  const solutionImageHandlers = makeSectionImageHandlers('detailedAnswerImages');

  function handlePromptImageChange(
    imageId: string,
    updates: Partial<EditableQuestionImage>,
  ) {
    updateDraft({
      promptImages: (draft.promptImages ?? []).map((image) =>
        image.id === imageId ? { ...image, ...updates } : image,
      ),
    });
  }

  function handleRemovePromptImage(imageId: string) {
    updateDraft({
      promptImages: (draft.promptImages ?? []).filter(
        (image) => image.id !== imageId,
      ),
    });
  }

  function handleChangeRubric(gradingRubric: EditableGradingRubric) {
    const normalizedRubric = normalizeGradingRubric(gradingRubric);

    updateDraft({
      gradingRubric: normalizedRubric,
      value: getRubricTotalScore(normalizedRubric),
    });
  }

  const promptImagesEditor = (
    <QuestionImagesEditor
      context="prompt"
      description="Images shown inside this question, in the order listed."
      disabled={disabled}
      heading="Prompt images"
      images={draft.promptImages ?? []}
      onImageChange={handlePromptImageChange}
      onRemoveImage={handleRemovePromptImage}
      onUploadComplete={handlePromptImageUpload}
      onUploadEnd={imageUploadCount.onUploadEnd}
      onUploadStart={imageUploadCount.onUploadStart}
    />
  );

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
          return;
        }

        requestClose();
      }}
      open={open}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          aria-describedby={undefined}
          className={styles.content}
          {...focusReturn}
          onEscapeKeyDown={(event) => {
            if (isDirty) {
              event.preventDefault();
              requestClose();
            }
          }}
          onInteractOutside={(event) => {
            if (isDirty) {
              event.preventDefault();
              requestClose();
            }
          }}
        >
          <header className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>{title}</Dialog.Title>
              <p className={styles.subtitle}>
                Edit the draft here, then use the page-level Save button to persist
                changes to the course.
              </p>
            </div>
            <button
              aria-label="Close question editor"
              className={styles.closeButton}
              onClick={requestClose}
              type="button"
            >
              ×
            </button>
          </header>

          <div aria-label="Question editor tabs" className={styles.tabs} role="tablist">
            <button
              aria-controls={editPanelId}
              aria-selected={activeTab === 'edit'}
              className={styles.tab}
              id={editTabId}
              onClick={() => setActiveTab('edit')}
              role="tab"
              type="button"
            >
              Edit
            </button>
            <button
              aria-controls={previewPanelId}
              aria-selected={activeTab === 'preview'}
              className={styles.tab}
              id={previewTabId}
              onClick={() => setActiveTab('preview')}
              role="tab"
              type="button"
            >
              Preview
            </button>
          </div>

          <div className={styles.body}>
            <div
              aria-labelledby={editTabId}
              hidden={activeTab !== 'edit'}
              id={editPanelId}
              role="tabpanel"
            >
              <div className={styles.form}>
                {structuralEditsRestricted ? (
                  <div
                    aria-labelledby={lockNoteTitleId}
                    className={styles.lockNote}
                    role="note"
                  >
                    <p className={styles.lockNoteTitle} id={lockNoteTitleId}>
                      Some controls are locked
                    </p>
                    <p>
                      Students can see this question, so these are locked: its type,
                      whether it uses randomized variables, adding or removing
                      options, parts or variables, and each part’s type and place.
                    </p>
                    <p>
                      You can still edit the prompt, the options’ text, the correct
                      answer, the tolerance, Points, Participation credit, the hint
                      and the solution.
                    </p>
                    <p>
                      A change to the correct answer, Points or Participation credit
                      applies to answers graded from now on. Run Regrade to apply it
                      to answers already graded.
                    </p>
                    {draft.useRandomVariables ? (
                      <p>
                        Students who have already answered keep their own random
                        values and correct answer, even after a Regrade. To change
                        their scores, give full credit or remove the question.
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <fieldset className={styles.typeSelector}>
                  <legend className={styles.sectionLabel}>Question type</legend>
                  <div className={styles.typeGrid}>
                    {QUESTION_TYPES.map((option) => (
                      <label
                        className={styles.typeOption}
                        data-selected={draft.type === option.value ? 'true' : 'false'}
                        key={option.value}
                      >
                        <input
                          checked={draft.type === option.value}
                          disabled={structuralDisabled}
                          name={`${draft.id}-type`}
                          onChange={() => {
                            const becomesMultiPart =
                              option.value === 'MULTI_PART' && draft.type !== 'MULTI_PART';
                            // AI Upload takes its rubric's total, whatever came before.
                            const restored =
                              draft.type === 'MULTI_PART' &&
                              option.value !== 'MULTI_PART' &&
                              option.value !== 'AI_UPLOAD'
                                ? pointsBeforeParts
                                : undefined;

                            if (becomesMultiPart) {
                              setPointsBeforeParts(draft.value);
                            }

                            setDraft((currentDraft) => {
                              const next = withTypeDefaults(currentDraft, option.value);

                              return restored === undefined ? next : { ...next, value: restored };
                            });
                          }}
                          title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
                          type="radio"
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                  {selectedType ? (
                    <p className={styles.typeDescription}>
                      {selectedType.description}
                    </p>
                  ) : null}
                </fieldset>

                {isUnsupported ? (
                  <p className={styles.errorMessage} role="alert">
                    This unsupported question type is preserved but cannot be edited
                    in the dialog yet.
                  </p>
                ) : (
                  <>
                    {draft.type !== 'MULTI_PART' ? (
                      <div className={styles.editorGroup}>
                        <p className={styles.sectionLabel}>Prompt</p>
                        <TextUnitEditor
                          label="Question prompt"
                          onChange={(contentHtml) => {
                            updateDraft({ contentHtml });
                          }}
                          placeholder="Write the question…"
                          value={draft.contentHtml}
                        />
                        {promptImagesEditor}
                      </div>
                    ) : null}

                    {draft.type === 'MULTIPLE_CHOICE' ||
                    draft.type === 'SELECT_ALL' ? (
                      <MultipleChoiceEditor
                        correctAnswerIndex={draft.correctAnswerIndex}
                        correctAnswerIndices={draft.correctAnswerIndices}
                        disabled={disabled}
                        groupName={`question-dialog-${draft.id}`}
                        multiSelect={draft.type === 'SELECT_ALL'}
                        onAddOption={() => {
                          updateDraft({ options: [...draft.options, ''] });
                        }}
                        onChangeCorrectAnswer={(index) => {
                          if (draft.type !== 'SELECT_ALL') {
                            updateDraft({ correctAnswerIndex: index });
                            return;
                          }

                          // Marking toggles rather than replaces.
                          const current = draft.correctAnswerIndices ?? [];
                          updateDraft({
                            correctAnswerIndices: current.includes(index)
                              ? current.filter((entry) => entry !== index)
                              : [...current, index].sort((a, b) => a - b),
                          });
                        }}
                        onChangeOption={(optionIndex, value) => {
                          updateDraft({
                            options: draft.options.map((option, currentIndex) =>
                              currentIndex === optionIndex ? value : option,
                            ),
                          });
                        }}
                        onRemoveOption={(optionIndex) => {
                          const nextOptions = draft.options.filter(
                            (_option, currentIndex) => currentIndex !== optionIndex,
                          );
                          const nextCorrectAnswerIndex =
                            draft.correctAnswerIndex === undefined
                              ? undefined
                              : draft.correctAnswerIndex === optionIndex
                                ? 0
                                : draft.correctAnswerIndex > optionIndex
                                  ? draft.correctAnswerIndex - 1
                                  : draft.correctAnswerIndex;

                          const nextCorrectAnswerIndices = (
                            draft.correctAnswerIndices ?? []
                          )
                            .filter((entry) => entry !== optionIndex)
                            .map((entry) =>
                              entry > optionIndex ? entry - 1 : entry,
                            );

                          updateDraft({
                            correctAnswerIndex: nextCorrectAnswerIndex,
                            correctAnswerIndices: nextCorrectAnswerIndices,
                            options: nextOptions,
                          });
                        }}
                        options={draft.options}
                        structuralEditsRestricted={structuralEditsRestricted}
                      />
                    ) : null}

                    {draft.type === 'NUMERICAL_WITH_ERROR' ? (
                      <NumericalAnswerEditor
                        answerUnits={draft.answerUnits}
                        disabled={disabled}
                        numericalAnswer={draft.numericalAnswer}
                        numericalError={draft.numericalError}
                        onChange={updateDraft}
                        structuralEditsRestricted={structuralEditsRestricted}
                        useRandomVariables={draft.useRandomVariables}
                        variableBasedAnswer={draft.variableBasedAnswer}
                      />
                    ) : null}

                    {draft.type === 'EXACT_ANSWER' ? (
                      <ExactAnswerEditor
                        answerUnits={draft.answerUnits}
                        disabled={disabled}
                        exactAnswer={draft.exactAnswer}
                        onChange={updateDraft}
                        structuralEditsRestricted={structuralEditsRestricted}
                        useRandomVariables={draft.useRandomVariables}
                        variableBasedAnswer={draft.variableBasedAnswer}
                      />
                    ) : null}

                    {draft.type === 'AI_UPLOAD' ? (
                      <AIUploadQuestionEditor
                        disabled={disabled}
                        gradingRubric={draft.gradingRubric}
                        onChange={handleChangeRubric}
                        validationError={draft.validationError}
                      />
                    ) : null}

                    {draft.type === 'MULTI_PART' ? (
                      <MultiPartEditor
                        disabled={disabled}
                        onAddPart={(type) => {
                          const parts = [...(draft.parts ?? []), createDraftQuestionPart(type)];

                          updateDraft({ ...followPartsTotal(draft, parts), parts });
                        }}
                        onRemovePart={(partIndex) => {
                          const parts = (draft.parts ?? []).filter(
                            (_part, currentIndex) => currentIndex !== partIndex,
                          );

                          updateDraft({ ...followPartsTotal(draft, parts), parts });
                        }}
                        onReorderPart={(fromIndex, toIndex) => {
                          updateDraft({
                            parts: moveArrayItem(draft.parts ?? [], fromIndex, toIndex),
                          });
                        }}
                        onUpdatePart={updateQuestionPart}
                        onUpdateQuestion={updateDraft}
                        afterStem={promptImagesEditor}
                        question={draft}
                        structuralEditsRestricted={structuralEditsRestricted}
                      />
                    ) : draft.type === 'AI_UPLOAD' ? null : (
                      <>
                        <label className={styles.checkboxField}>
                          <input
                            checked={draft.useRandomVariables ?? false}
                            disabled={structuralDisabled}
                            onChange={(event) => {
                              updateDraft({ useRandomVariables: event.target.checked });
                            }}
                            title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
                            type="checkbox"
                          />
                          <span>Use randomized variables</span>
                        </label>

                        {draft.useRandomVariables ? (
                          <VariablesPanel
                            detectedVariableNames={detectedVariableNames}
                            disabled={disabled}
                            onAddVariable={() => {
                              updateDraft({
                                useRandomVariables: true,
                                variables: [
                                  ...draft.variables,
                                  {
                                    id: `${draft.id}-variable-${draft.variables.length + 1}`,
                                    name: `variable_${draft.variables.length + 1}`,
                                    type: 'NUMERICAL',
                                  },
                                ],
                              });
                            }}
                            onRemoveVariable={(variableIndex) => {
                              updateDraft({
                                variables: draft.variables.filter(
                                  (_variable, currentIndex) =>
                                    currentIndex !== variableIndex,
                                ),
                              });
                            }}
                            onUpdateVariable={(variableIndex, updates) => {
                              updateDraft({
                                variables: draft.variables.map((variable, currentIndex) =>
                                  currentIndex === variableIndex
                                    ? {
                                        ...variable,
                                        ...updates,
                                      }
                                    : variable,
                                ),
                              });
                            }}
                            structuralEditsRestricted={structuralEditsRestricted}
                            variables={draft.variables}
                          />
                        ) : null}
                      </>
                    )}

                    <div className={styles.metadataGrid}>
                      <label className={styles.field}>
                        <span className={styles.label}>Points</span>
                        <input
                          aria-describedby={partsTotal === undefined ? undefined : pointsNoteId}
                          className={styles.input}
                          disabled={disabled || draft.type === 'AI_UPLOAD'}
                          min="0"
                          onChange={(event) => {
                            updateDraft({
                              value:
                                event.target.value === ''
                                  ? undefined
                                  : Number(event.target.value),
                            });
                          }}
                          // A multi-part question's points come from its parts.
                          readOnly={partsTotal !== undefined}
                          ref={pointsFieldRef}
                          step="0.5"
                          type="number"
                          value={draft.value ?? ''}
                        />
                      </label>

                      <label className={styles.checkboxField}>
                        <input
                          checked={draft.creditForParticipation ?? false}
                          disabled={disabled}
                          onChange={(event) => {
                            updateDraft({
                              creditForParticipation: event.target.checked,
                            });
                          }}
                          type="checkbox"
                        />
                        <span>Participation credit</span>
                      </label>

                      {partsTotal === undefined ? null : pointsDiffer ? (
                        <div className={styles.pointsNote}>
                          <p id={pointsNoteId}>
                            Its parts add up to {formatPoints(partsTotal)}. It keeps its{' '}
                            {formatPoints(draft.value ?? 0)} until a part&apos;s points
                            change.
                          </p>
                          <Button
                            disabled={disabled}
                            onClick={() => {
                              updateDraft({ value: partsTotal });
                              // This button goes with the difference: focus
                              // moves to the points it changed.
                              pointsFieldRef.current?.focus();
                            }}
                            size="small"
                            variant="secondary"
                          >
                            Use the parts&apos; total
                          </Button>
                        </div>
                      ) : (
                        <p className={styles.pointsNote} id={pointsNoteId}>
                          The total of its parts&apos; points.
                        </p>
                      )}
                    </div>

                    {draft.type !== 'MULTI_PART' ? (
                      <HintSolutionEditor
                        detailedAnswerHtml={draft.detailedAnswerHtml}
                        detailedAnswerImages={draft.detailedAnswerImages ?? []}
                        disabled={disabled}
                        hintHtml={draft.hintHtml}
                        hintImages={draft.hintImages ?? []}
                        onDetailedAnswerChange={(detailedAnswerHtml) => {
                          updateDraft({ detailedAnswerHtml });
                        }}
                        onDetailedAnswerImageChange={solutionImageHandlers.onChange}
                        onDetailedAnswerImageRemove={solutionImageHandlers.onRemove}
                        onDetailedAnswerImageUpload={solutionImageHandlers.onUpload}
                        onHintChange={(hintHtml) => {
                          updateDraft({ hintHtml });
                        }}
                        onHintImageChange={hintImageHandlers.onChange}
                        onHintImageRemove={hintImageHandlers.onRemove}
                        onHintImageUpload={hintImageHandlers.onUpload}
                        onImageUploadEnd={imageUploadCount.onUploadEnd}
                        onImageUploadStart={imageUploadCount.onUploadStart}
                      />
                    ) : null}
                  </>
                )}

                {draft.validationError ? (
                  <p className={styles.errorMessage} role="alert">
                    {draft.validationError}
                  </p>
                ) : null}
              </div>
            </div>

            <div
              aria-labelledby={previewTabId}
              hidden={activeTab !== 'preview'}
              id={previewPanelId}
              role="tabpanel"
            >
              <QuestionReadonlyPreview
                displayLabel={questionLabel}
                question={draft}
              />
            </div>
          </div>

          <footer className={styles.footer}>
            {/* There from the start, so that a screen reader hears the reason. */}
            <p className={styles.saveReason} id={saveReasonId} role="status">
              {uploadsUnderway === 1
                ? 'Wait for the image to finish uploading.'
                : uploadsUnderway > 1
                  ? 'Wait for the images to finish uploading.'
                  : ''}
            </p>
            <Button
              onClick={requestClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              aria-describedby={uploadsUnderway > 0 ? saveReasonId : undefined}
              disabled={disabled || isUnsupported || uploadsUnderway > 0}
              onClick={handleSave}
              variant="primary"
            >
              Save
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
